import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";

export default function RecusList() {

  const [recus, setRecus] =
    useState([]);

  const [search, setSearch] =
    useState("");

  const [filter, setFilter] =
    useState("all");

  const [page, setPage] =
    useState(1);

  const limit = 20;

  const navigate =
    useNavigate();

  useEffect(() => {
    loadRecus();
  }, []);

  // =====================
  // FETCH
  // =====================

  const loadRecus = async () => {

    try {

      const res =
        await api.get("/recus");

      setRecus(res.data);

    } catch (err) {

      console.error(err);

    }
  };

  // =====================
  // OPEN
  // =====================

  const openRecu = (id) => {
    navigate(`/recu/${id}`);
  };

  const today =
    new Date();

  // =====================
  // FILTERS
  // =====================

  const filtered =
    recus.filter((r) => {

      const matchSearch = `
        ${r.numero}
        ${r.nom}
        ${r.prenom}
        ${r.appartement}
      `
        .toLowerCase()
        .includes(
          search.toLowerCase()
        );

      const dateValue =
        r.date_recu ||
        r.date_paiement;

      const date = dateValue
        ? new Date(dateValue)
        : null;

      if (filter === "today") {

        return (
          matchSearch &&
          date &&
          date.toDateString() ===
            today.toDateString()
        );

      }

      if (filter === "week") {

        const diff = date
          ? (today - date) /
            (1000 *
              60 *
              60 *
              24)
          : 999;

        return (
          matchSearch &&
          diff <= 7
        );

      }

      return matchSearch;
    });

  // =====================
  // PAGINATION
  // =====================

  const totalPages =
    Math.ceil(
      filtered.length / limit
    );

  const paginated =
    filtered.slice(
      (page - 1) * limit,
      page * limit
    );

  // =====================
  // STATS
  // =====================

  const totalMontant =
    filtered.reduce(
      (sum, r) =>
        sum +
        Number(
          r.montant || 0
        ),
      0
    );

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 min-h-[100dvh] bg-gray-100">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between gap-3">

        <h2 className="text-2xl md:text-3xl font-semibold">
          Reçus
        </h2>

        <input
          type="text"
          placeholder="Rechercher..."
          className="border px-4 py-3 rounded-xl w-full md:w-72 outline-none focus:ring-2 focus:ring-green-400"
          value={search}
          onChange={(e) => {
            setSearch(
              e.target.value
            );

            setPage(1);
          }}
        />

      </div>

      {/* FILTERS */}
      <div className="flex gap-2 flex-wrap">

        <button
          onClick={() => {
            setFilter("all");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            filter === "all"
              ? "bg-gray-800 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          Tous
        </button>

        <button
          onClick={() => {
            setFilter("today");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            filter === "today"
              ? "bg-blue-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          Aujourd’hui
        </button>

        <button
          onClick={() => {
            setFilter("week");
            setPage(1);
          }}
          className={`px-4 py-2 rounded-xl text-sm transition ${
            filter === "week"
              ? "bg-green-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          7 jours
        </button>

      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">

        <div className="bg-white p-4 rounded-2xl shadow text-center">

          <p className="text-sm text-gray-500">
            Total encaissé
          </p>

          <p className="font-bold text-lg md:text-xl">
            {totalMontant.toLocaleString()} $
          </p>

        </div>

        <div className="bg-green-100 p-4 rounded-2xl text-center">

          <p className="text-sm">
            Nombre de reçus
          </p>

          <p className="font-bold text-green-700 text-lg md:text-xl">
            {filtered.length}
          </p>

        </div>

      </div>

      {/* MOBILE */}
      <div className="grid gap-3 md:hidden">

        {paginated.map((r) => {

          const dateValue =
            r.date_recu ||
            r.date_paiement;

          return (
            <div
              key={r.id}
              className="bg-white p-4 rounded-2xl shadow border cursor-pointer"
              onClick={() =>
                openRecu(r.id)
              }
            >

              <div className="flex justify-between items-start gap-2">

                <span className="font-semibold text-green-600 font-mono tracking-wide break-all">
                  {r.numero}
                </span>

                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {dateValue
                    ? new Date(
                        dateValue
                      ).toLocaleDateString()
                    : "-"}
                </span>

              </div>

              <p className="text-sm mt-3 break-words">
                👤 {r.nom}{" "}
                {r.prenom}
              </p>

              <p className="text-sm break-words">
                🏢 Apt{" "}
                {r.appartement}
              </p>

              <div className="mt-3 font-bold text-xl text-green-600">
                {Number(
                  r.montant || 0
                ).toLocaleString()} $
              </div>

            </div>
          );
        })}

      </div>

      {/* DESKTOP */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-2xl shadow">

        <table className="w-full text-sm">

          <thead className="bg-gray-900 text-white">

            <tr>

              <th className="px-5 py-3 text-left">
                Date
              </th>

              <th className="px-5 py-3 text-left">
                Appartement
              </th>

              <th className="px-5 py-3 text-left">
                N° Reçu
              </th>

              <th className="px-5 py-3 text-right pr-8">
                Montant
              </th>

              <th className="px-5 py-3 text-left pl-8">
                Client
              </th>

            </tr>

          </thead>

          <tbody>

            {paginated.map((r) => {

              const dateValue =
                r.date_recu ||
                r.date_paiement;

              return (
                <tr
                  key={r.id}
                  onClick={() =>
                    openRecu(r.id)
                  }
                  className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer transition"
                >

                  <td className="px-5 py-3 whitespace-nowrap">
                    {dateValue
                      ? new Date(
                          dateValue
                        ).toLocaleDateString()
                      : "-"}
                  </td>

                  <td className="px-5 py-3 whitespace-nowrap">
                    {r.appartement}
                  </td>

                  <td className="px-5 py-3 text-green-600 font-semibold font-mono tracking-wide whitespace-nowrap">
                    {r.numero}
                  </td>

                  <td className="px-5 py-3 text-right font-bold pr-8 whitespace-nowrap">
                    {Number(
                      r.montant || 0
                    ).toLocaleString()} $
                  </td>

                  <td className="px-5 py-3 pl-8">
                    {r.nom}{" "}
                    {r.prenom}
                  </td>

                </tr>
              );
            })}

          </tbody>

        </table>

      </div>

      {/* PAGINATION */}
      <div className="flex justify-center gap-2 flex-wrap">

        {Array.from(
          { length: totalPages },
          (_, i) => (
            <button
              key={i}
              onClick={() =>
                setPage(i + 1)
              }
              className={`px-4 py-2 rounded-xl text-sm transition ${
                page === i + 1
                  ? "bg-green-600 text-white"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          )
        )}

      </div>

    </div>
  );
}